import { CARD_ELEVATION } from '@/components/page-card.styles';
import { cn } from '@/lib/utils';

/**
 * The Clu dock's frame, shared by the dock and by its crash fallback so the
 * fallback stands exactly where the dock stood and the page beside it never
 * moves.
 */

/** The dock's width until the user drags it. */
export const DEFAULT_DOCK_WIDTH = 460;

/**
 * The outer box: a flex item in the shell's row, its left margin clearing the
 * fixed tabbed sidebar (256px open, 64px folded), on the sidebar's tint, with
 * a 2px gutter on the right. With no page beside it (an agent's chat alone)
 * it keeps the page's right inset.
 *
 * It is part of the chrome (`app-chrome`), which the appearance tints with
 * the sidebar and the page shell.
 *
 * The margins follow `data-sidebar-collapsed` on <html> in CSS, as the page's
 * does, so the dock slides beside the sidebar as it opens and folds without a
 * render. A negative right margin as wide as the left one gives the nav's
 * width back, so the frame takes only its own width in the row and the page
 * after it keeps its own nav margin: the page starts at the dock's right edge
 * while the dock shows, and never moves as the frame mounts at width 0 or
 * leaves from it.
 *
 * It stacks at z-40, under the tabbed sidebar (z-41): the sidebar's folded
 * rail pops the What's new card out over the dock, which the frame would
 * otherwise paint over.
 */
export function dockFrameClass({
  pageAside = false,
}: {
  pageAside?: boolean;
} = {}): string {
  return cn(
    'relative z-40 flex min-h-0 shrink-0 flex-col',
    // app-chrome: the appearance's gradient and grain lie on it too.
    'app-chrome -mr-[256px] ml-[256px] bg-[hsl(var(--sidebar-tint))] py-2',
    'in-data-[sidebar-collapsed="true"]:ml-16',
    'in-data-[sidebar-collapsed="true"]:-mr-16',
    'transition-[margin] duration-200 ease-out',
    pageAside ? 'pr-2' : 'pr-0.5'
  );
}

/**
 * The floating card inside the frame, at the page card's elevation. It stands
 * in the page card's place beside the sidebar, which draws its left shadow
 * (see TabsSidebar).
 */
export function dockCardClass(): string {
  return cn(
    'relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-background',
    CARD_ELEVATION
  );
}
